import type { CompanyFollower } from "@/modules/companies/client/api/types"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useParams } from "next/navigation"

import {
  COMPANY_FOLLOWER_QUERY_KEY,
  setFollowCompanyStatus,
} from "@/modules/companies/client/api/endpoints/companyFollower"

import { useCompanyFetch } from "./useCompanyFetch"

type FollowCompanyFetch = {
  followCompany: (follow: boolean) => Promise<void>
  isLoadingFollowCompany: boolean
  isSuccessFollowCompany: boolean
  isErrorFollowCompany: boolean
}

export const useFollowCompany = (): FollowCompanyFetch => {
  const queryClient = useQueryClient() // not using createComponentQueryClient() as it doesn't work
  const { companySlug } = useParams<{ companySlug: string }>()
  const { dataCompany } = useCompanyFetch()
  const companyId = dataCompany?.id || ""

  const followCompanyMutation = useMutation({
    mutationFn: setFollowCompanyStatus,
    onError: error => {
      console.error(`Error while following company.\n${error}`)
    },
    onSuccess: (_data, { follow }) => {
      const dataCompanyFollower = queryClient.getQueryData<CompanyFollower>([
        COMPANY_FOLLOWER_QUERY_KEY,
        dataCompany?.id,
        companySlug,
      ])
      queryClient.setQueryData([COMPANY_FOLLOWER_QUERY_KEY, dataCompany?.id, companySlug], {
        ...dataCompanyFollower,
        followers: +(dataCompanyFollower?.followers || 0) + (follow ? 1 : -1),
        userInfo: {
          ...dataCompanyFollower?.userInfo,
          followsCompany: follow,
        },
      })
    },
  })

  const followCompany = async (follow: boolean): Promise<void> => {
    await followCompanyMutation.mutateAsync({ company_id: companyId, follow })
  }

  return {
    followCompany,
    isErrorFollowCompany: followCompanyMutation.isError,
    isLoadingFollowCompany: followCompanyMutation.isPending,
    isSuccessFollowCompany: followCompanyMutation.isSuccess,
  }
}
